import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import SiteCTA from '@/components/SiteCTA';
import { Icon } from '@iconify/react';
import { 
  ArrowRight, 
  Clock, 
  Zap, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  Activity,
  Target,
  Sparkles,
  Timer,
  Rocket,
  ChevronRight,
  Calendar,
  Users,
  BarChart3,
  Compass,
  HeartHandshake,
  Stethoscope,
  Wrench,
  LineChart,
  Ambulance,
  Map,
  Award,
  GitMerge,
  Database,
  Briefcase,
  FileSearch,
  Shield,
  RefreshCw,
  Brain
} from 'lucide-react';

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-40 right-10 w-96 h-96 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 60, 0],
          y: [0, -40, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-10 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -40, 0],
          y: [0, 60, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

// Solution card component
const SolutionCard = ({ solution, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.15 }}
      whileHover={{ scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative group perspective-1000"
    >
      <div className="relative h-full">
        {/* Glow effect */}
        <motion.div
          className="absolute -inset-0.5 bg-gradient-to-r from-gold via-amber-400 to-gold rounded-2xl opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500"
          animate={isHovered ? { opacity: 0.7 } : { opacity: 0 }}
        />
        
        {/* Main card */}
        <div className="relative h-full bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl border border-gold/20 overflow-hidden backdrop-blur-xl">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]" />
          </div>
          
          {/* Content */}
          <div className="relative p-8 h-full flex flex-col">
            {/* Header with icon */}
            <div className="flex items-start justify-between mb-6">
              <motion.div
                className="w-16 h-16 bg-gradient-to-br from-gold to-amber-400 rounded-2xl flex items-center justify-center shadow-2xl"
                animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}}
                transition={{ duration: 0.5 }}
              >
                <Icon icon={solution.icon} className="w-8 h-8 text-black" />
              </motion.div>
              {solution.urgent && (
                <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold uppercase">
                  Urgent
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
              {solution.title}
            </h3>

            {/* Description */}
            <p className="text-gray-300 text-base leading-relaxed mb-6 flex-grow">
              {solution.description}
            </p>

            {/* Ideal For */}
            <div className="mb-6">
              <p className="text-amber-400 font-semibold text-sm mb-2 uppercase tracking-wider">Ideal For:</p>
              <p className="text-gray-400 text-sm">{solution.idealFor}</p>
            </div>

            {/* Key Benefits */}
            <div className="space-y-2 mb-6">
              {solution.benefits.map((benefit, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: index * 0.1 + idx * 0.05 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                  <span className="text-sm text-gray-400">{benefit}</span>
                </motion.div>
              ))}
            </div>

            {/* Footer with timeline */}
            <motion.div 
              className="mt-auto pt-6 border-t border-gold/20 flex items-center justify-between"
              initial={{ opacity: 0.7 }}
              animate={isHovered ? { opacity: 1 } : { opacity: 0.7 }}
            >
              <div className="flex items-center gap-3">
                <Timer className="w-5 h-5 text-gold" />
                <span className="text-white font-medium">{solution.timeline}</span>
              </div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-gold text-sm font-semibold cursor-pointer"
              >
                Learn More →
              </motion.div>
            </motion.div>
          </div>

          {/* Animated corner accent */}
          <motion.div
            className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold/20 to-transparent"
            animate={isHovered ? { scale: 1.2 } : { scale: 1 }}
            style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
          />
        </div>
      </div>
    </motion.div>
  );
};

// Feature component
const FeatureCard = ({ icon, title, description, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay }}
      className="text-center"
    >
      <motion.div
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.5 }}
        className="w-20 h-20 bg-gradient-to-br from-gold to-amber-400 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl"
      >
        <Icon icon={icon} className="w-10 h-10 text-black" />
      </motion.div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm">{description}</p>
    </motion.div>
  );
};

const PrecisionPMOPage = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  
  const precisionSolutions = [
    {
      title: 'Crisis Recovery',
      icon: 'solar:danger-triangle-bold',
      description: 'When projects are failing and immediate intervention is critical.',
      idealFor: 'Projects in red status needing urgent turnaround',
      benefits: [
        'Rapid assessment and stabilization',
        'Clear recovery roadmap',
        'Executive confidence restoration',
        'Risk mitigation strategies'
      ],
      timeline: '1-3 weeks',
      urgent: true
    },
    {
      title: 'Process Optimization',
      icon: 'solar:settings-minimalistic-bold',
      description: 'Streamline workflows and eliminate bottlenecks slowing your delivery.',
      idealFor: 'Teams struggling with inefficient processes',
      benefits: [
        'Workflow automation opportunities',
        'Reduced cycle times',
        'Enhanced team productivity',
        'Clear process documentation'
      ],
      timeline: '2-4 weeks',
      urgent: false
    },
    {
      title: 'Portfolio Alignment',
      icon: 'solar:chart-2-bold',
      description: 'Ensure your projects directly support strategic business objectives.',
      idealFor: 'Organizations with scattered project investments',
      benefits: [
        'Strategic priority mapping',
        'Resource reallocation guidance',
        'Investment optimization',
        'Clear go/no-go criteria'
      ],
      timeline: '2-3 weeks',
      urgent: false
    },
    {
      title: 'Governance Gaps',
      icon: 'solar:shield-check-bold',
      description: 'Establish decision rights and accountability structures that work.',
      idealFor: 'PMOs lacking clear authority and processes',
      benefits: [
        'Defined RACI matrices',
        'Escalation pathways',
        'Committee structures',
        'Decision frameworks'
      ],
      timeline: '2-4 weeks',
      urgent: false
    },
    {
      title: 'Tool Implementation',
      icon: 'solar:laptop-minimalistic-bold',
      description: 'Select and deploy the right PM tools for your organization.',
      idealFor: 'Teams using spreadsheets or outdated systems',
      benefits: [
        'Tool selection criteria',
        'Implementation roadmap',
        'Training programs',
        'Adoption strategies'
      ],
      timeline: '4-6 weeks',
      urgent: false
    },
    {
      title: 'Reporting Excellence',
      icon: 'solar:presentation-graph-bold',
      description: 'Create executive dashboards that drive informed decisions.',
      idealFor: 'Leaders lacking project visibility',
      benefits: [
        'Executive dashboard design',
        'KPI framework development',
        'Automated reporting',
        'Real-time insights'
      ],
      timeline: '2-3 weeks',
      urgent: false
    }
  ];

  const features = [
    { icon: 'solar:bolt-circle-bold', title: 'Rapid Response', description: 'Deploy within 48 hours for critical needs' },
    { icon: 'solar:target-bold', title: 'Laser Focus', description: 'Address specific pain points without disruption' },
    { icon: 'solar:dollar-minimalistic-bold', title: 'Cost Effective', description: 'Get expert help without long-term commitments' },
    { icon: 'solar:graph-up-bold', title: 'Immediate Impact', description: 'See measurable results within weeks' },
  ];

  return (
    <>
      <div className="min-h-screen bg-black text-white overflow-hidden">
        {/* Hero Section with Parallax */}
        <section className="relative min-h-screen flex items-center justify-center">
          <AnimatedBackground />
          
          <motion.div 
            style={{ y }}
            className="container mx-auto px-4 relative z-10 py-32"
          >
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center max-w-5xl mx-auto"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
              >
                <Sparkles className="w-5 h-5 text-gold" />
                <span className="text-gold font-bold text-sm uppercase tracking-wider">On-Demand Excellence</span>
                <Sparkles className="w-5 h-5 text-gold" />
              </motion.div>
              
              {/* Main heading with gradient */}
              <motion.h1 
                className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold">
                  PMO Power.
                </span>
                <br />
                <span className="text-white text-4xl md:text-5xl lg:text-6xl">
                  On Demand.
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-12 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Precision PMO solutions that target your specific challenges. 
                Get expert intervention exactly when and where you need it most.
              </motion.p>

              {/* Stats */}
              <motion.div 
                className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                {[
                  { number: '48hrs', label: 'Rapid Deployment' },
                  { number: '2-6', label: 'Week Engagements' },
                  { number: '100%', label: 'Success Rate' },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl md:text-4xl font-bold text-gold mb-2">{stat.number}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="mt-12"
              >
                <Button asChild size="lg" className="bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black px-10 py-7 text-lg rounded-full shadow-2xl hover:shadow-gold/50 transition-all font-bold">
                  <Link to="/contact" className="flex items-center gap-3">
                    Get Immediate Help
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-gold rounded-full flex justify-center">
              <div className="w-1 h-3 bg-gold rounded-full mt-2" />
            </div>
          </motion.div>
        </section>

        {/* Features Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {features.map((feature, index) => (
                <FeatureCard key={feature.title} {...feature} delay={index * 0.1} />
              ))}
            </div>
          </div>
        </section>

        {/* Solutions Grid Section */}
        <section className="py-20 relative bg-gradient-to-b from-black via-gray-900 to-black">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  Precision Solutions
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Targeted interventions for your most pressing PMO challenges
              </p>
            </motion.div>

            {/* Solutions Grid */}
            <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {precisionSolutions.map((solution, index) => (
                <SolutionCard key={solution.title} solution={solution} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  How It Works
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                From problem to solution in record time
              </p>
            </motion.div>

            {/* Process Steps */}
            <div className="max-w-4xl mx-auto">
              {[
                { day: 'Hour 1', title: 'Initial Contact', desc: 'Describe your challenge via our rapid intake process' },
                { day: 'Hours 2-24', title: 'Expert Match', desc: 'We assign the perfect consultant for your specific need' },
                { day: 'Day 2', title: 'Deep Dive', desc: 'Comprehensive assessment of your situation' },
                { day: 'Days 3-5', title: 'Solution Design', desc: 'Create targeted action plan with clear deliverables' },
                { day: 'Week 2+', title: 'Implementation', desc: 'Execute solution with hands-on support' },
              ].map((step, index) => (
                <motion.div
                  key={step.day}
                  className="flex items-center gap-8 mb-8"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-gold to-amber-400 rounded-full flex items-center justify-center">
                      <span className="text-black font-bold text-2xl">{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="bg-gradient-to-r from-gray-900 to-transparent p-6 rounded-2xl border-l-4 border-gold">
                      <div className="text-gold font-bold mb-2">{step.day}</div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-gray-400">{step.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 relative bg-gradient-to-b from-black via-gray-900 to-black">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  Perfect For
                </span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { 
                  icon: 'solar:fire-bold', 
                  title: 'Fire Fighting', 
                  desc: 'When projects are burning and you need immediate help' 
                },
                { 
                  icon: 'solar:rocket-2-bold', 
                  title: 'Quick Wins', 
                  desc: 'Achieve rapid improvements without long engagements' 
                },
                { 
                  icon: 'solar:heart-pulse-bold', 
                  title: 'Health Checks', 
                  desc: 'Get expert assessment of your PMO performance' 
                },
                { 
                  icon: 'solar:magnifer-zoom-in-bold', 
                  title: 'Gap Analysis', 
                  desc: 'Identify and close critical capability gaps' 
                },
                { 
                  icon: 'solar:lightbulb-bolt-bold', 
                  title: 'Innovation', 
                  desc: 'Inject fresh thinking and best practices' 
                },
                { 
                  icon: 'solar:users-group-two-rounded-bold', 
                  title: 'Surge Capacity', 
                  desc: 'Scale expertise for critical initiatives' 
                },
              ].map((useCase, index) => (
                <motion.div
                  key={useCase.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-900/80 to-black/80 p-6 rounded-2xl border border-gold/20 hover:border-gold/40 transition-all group"
                >
                  <Icon icon={useCase.icon} className="w-12 h-12 text-gold mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold text-white mb-2">{useCase.title}</h3>
                  <p className="text-gray-400">{useCase.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold">
                  Don't Let Problems Fester
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Get expert PMO help exactly when you need it. No long contracts, just results.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black px-12 py-8 text-xl rounded-full shadow-2xl hover:shadow-gold/50 transition-all font-bold">
                <Link to="/contact" className="flex items-center gap-3">
                  Request Rapid Response
                  <ArrowRight className="w-6 h-6" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      </div>

      <SiteCTA />
    </>
  );
};

export default PrecisionPMOPage;